import { BTInputField } from '@common-components/bt-input-field/bt-input-field';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { TeamInboxActions } from './redux/TeamInboxReducer';
import { Search as SearchIcon } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classes from './team-inbox.module.scss';
import ChatsSideBarFolders from './chats-side-bar-folders';

const ChatsSideBarFixedHeader = React.memo(() => {
  const { t } = useTranslation();
  const chatsSidebarSearchToken = useAppSelector((state) => state.TeamInboxReducer?.chatsSidebarSearchToken);
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the raw input in local state and debounce the store dispatch so the
  // chat-list filtering doesn't re-run on every keystroke.
  const [localToken, setLocalToken] = useState(chatsSidebarSearchToken ?? '');
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync local state when the store token changes (e.g. external reset).
  useEffect(() => {
    setLocalToken(chatsSidebarSearchToken ?? '');
  }, [chatsSidebarSearchToken]);

  useEffect(() => () => clearTimeout(debounceTimerRef.current), []);

  const handleChange = (value: string) => {
    setLocalToken(value);
    clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      dispatch(TeamInboxActions.setChatsSidebarSearchToken(value));
    }, 250);
  };

  return (
    <>
      <Stack spacing={1.5} sx={{ padding: 2, backgroundColor: 'var(--wa-sidebar-bg)', borderBottom: '1px solid var(--wa-divider)' }}>
        <Stack
          direction="row"
          // wrap so a third pill (the debug read-receipt toggle) flows onto its
          // own line instead of being clipped by the sidebar edge.
          sx={{ alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}
        >
          <Typography
            variant="h6"
            className={classes.chatsSideBarTitle}
            sx={{ flexShrink: 0 }}
          >
            {t('teamInbox.messages')}
          </Typography>
          <ChatsSideBarFolders />
        </Stack>
        <BTInputField
          value={localToken}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              clearTimeout(debounceTimerRef.current);
              setLocalToken('');
              dispatch(TeamInboxActions.resetChatsSidebarSearchToken());
              inputRef.current?.blur();
            }
          }}
          placeholder={t('teamInbox.search', 'Search')}
          startAdornment={<SearchIcon className={classes.searchIcon} />}
          sx={{
            // WhatsApp Web search pill: flat filled field, no visible outline.
            backgroundColor: 'var(--wa-search-bg)',
            height: '35px',
            borderRadius: '8px',
            color: 'var(--wa-title-text)',
            '.MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '&:hover .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& input::placeholder': {
              color: 'var(--wa-secondary-text)',
              opacity: 1,
              fontSize: '14px',
              fontWeight: 400,
            },
          }}
        />
      </Stack>
    </>
  );
});

export default ChatsSideBarFixedHeader;